<template>
	<div v-if="!mobile && layout !== 'panel'" class="filters-container">
		<div class="filters filters-main">
			<slot name="prepend" />

			<FilterField v-for="filter in mainFilters" :key="filter.key" :filter="filter" @ref="(el) => setControl(filter.key, el)" @change="onControlChange" />

			<transition name="filters-fold">
				<div v-show="showAdditional" v-if="additionalFilters.length" class="filters filters-additional">
					<FilterField v-for="filter in additionalFilters" :key="filter.key" :filter="filter" @ref="(el) => setControl(filter.key, el)" @change="onControlChange" />
				</div>
			</transition>
		</div>

		<div class="filters-actions">
			<v-btn v-if="showMoreToggle && additionalFilters.length" @click="emit('update:showAdditional', !showAdditional)" color="secondary" variant="tonal" rounded="xl">
				<v-icon>{{ showAdditional ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
				{{ showAdditional ? t('Less') : t('More') }}
			</v-btn>
			<v-btn @click="emit('reset')" color="danger" variant="text" rounded="xl">
				<v-icon>filter_alt_off</v-icon>
				{{ t('Clear') }}
			</v-btn>
			<v-btn @click="emit('apply')" color="primary" variant="tonal" rounded="xl">
				<v-icon>filter_alt</v-icon>
				{{ t('Apply') }}
			</v-btn>
		</div>
	</div>

	<DialogControl v-else :model-value="open" :mobile="mobile" :eager="!mobile" :attach="overlayTarget || undefined" :class="mobile ? 'filter-controls-sheet-overlay' : 'filter-controls-panel-overlay'" :content-class="mobile ? undefined : 'filter-controls-panel-content'" @update:model-value="emit('update:open', $event)">
		<v-card :class="mobile ? 'filter-controls-sheet' : 'filter-controls-panel'" :flat="!mobile">
			<v-card-title :class="mobile ? 'filter-controls-sheet__title' : 'filter-controls-panel__title'">
				<v-icon>filter_list</v-icon>
				{{ t('Filters') }}
				<v-chip v-if="activeCount > 0" size="small" color="primary" variant="tonal">{{ activeCount }}</v-chip>
				<v-spacer></v-spacer>
				<v-btn icon="close" variant="tonal" color="primary" density="comfortable" @click="emit('update:open', false)" />
			</v-card-title>

			<v-card-text v-if="mobile" class="filter-controls-sheet__body">
				<slot name="prepend" />

				<FilterField v-for="filter in allFilters" :key="filter.key" :filter="filter" @ref="(el) => setControl(filter.key, el)" @change="onControlChange" />
			</v-card-text>

			<v-card-text v-else class="filter-controls-panel__body">
				<div v-if="toolbarFilters.length" class="filter-controls-panel__toolbar">
					<slot name="prepend" />
					<FilterField v-for="filter in toolbarFilters" :key="filter.key" :filter="filter" @ref="(el) => setControl(filter.key, el)" @change="onControlChange" />
				</div>

				<div v-for="section in panelSections" :key="section.key" class="filter-controls-panel__section">
					<div class="filter-controls-panel__section-title">{{ section.label }}</div>
					<div class="filter-controls-panel__section-grid">
						<FilterField v-for="filter in section.filters" :key="filter.key" :filter="filter" @ref="(el) => setControl(filter.key, el)" @change="onControlChange" />
					</div>
				</div>
			</v-card-text>

			<v-card-actions :class="mobile ? 'filter-controls-sheet__actions' : 'filter-controls-panel__actions'">
				<v-spacer></v-spacer>
				<v-btn variant="text" color="error" @click="emit('reset')">{{ t('Reset') }}</v-btn>
				<v-btn variant="tonal" color="primary" @click="emit('apply')">{{ t('Apply') }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>
</template>

<script setup>
import { computed, inject, reactive, ref, watch } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import FilterField from '@/components/controls/FilterField.vue';
import DialogControl from '@/components/controls/DialogControl.vue';

const props = defineProps({
	filters: { type: Array, default: () => [] },
	mobile: { type: Boolean, default: false },
	// 'inline' (default, today's desktop row — SpsBuilder.vue never passes this, byte-for-byte unaffected)
	// or 'panel' (new laptop side panel, converging with the mobile bottom-sheet mental model below).
	layout: { type: String, default: 'inline' },
	open: { type: Boolean, default: false },
	showAdditional: { type: Boolean, default: false },
	showMoreToggle: { type: Boolean, default: false },
});

const emit = defineEmits(['update:open', 'update:showAdditional', 'update:activeCount', 'apply', 'reset']);

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);

// Child instances, republished for the parent: <script setup> components are closed, and Vue has no
// forwardRef, so the builders reach their controls through this map instead of their own template refs.
const controls = reactive({});
const refreshVersion = ref(0);

const allFilters = computed(() => (Array.isArray(props.filters) ? props.filters : []).filter((filter) => filter?.key));
const mainFilters = computed(() => allFilters.value.filter((filter) => !filter.additional));
const additionalFilters = computed(() => allFilters.value.filter((filter) => filter.additional));

// Panel layout only: filters tagged `toolbar: true` (e.g. the KPI/measure select) render in their own
// strip above the 3 sections instead of inside one — visually "not a filter" per RG-03, though it still
// flows through the exact same control-registration plumbing as every other filter below.
const toolbarFilters = computed(() => allFilters.value.filter((filter) => filter.toolbar));
const PANEL_SECTION_LABEL_KEYS = {
	perimeter: 'FilterSectionPerimeter',
	pointOfSale: 'FilterSectionPointOfSale',
	product: 'FilterSectionProduct',
};
const panelSections = computed(() =>
	Object.keys(PANEL_SECTION_LABEL_KEYS)
		.map((sectionKey) => ({
			key: sectionKey,
			label: t(PANEL_SECTION_LABEL_KEYS[sectionKey]),
			filters: allFilters.value.filter((filter) => !filter.toolbar && (filter.section || 'perimeter') === sectionKey),
		}))
		.filter((section) => section.filters.length > 0)
);

// isDirty() reads a computed inside the child, so this stays reactive across the component boundary;
// required controls (the KPI reference) are never counted as an active filter.
const activeCount = computed(() => {
	void refreshVersion.value;
	return allFilters.value.reduce((count, filter) => {
		const control = controls[filter.key];
		if (!control || typeof control.isDirty !== 'function') return count;
		if (typeof control.isRequired === 'function' && control.isRequired()) return count;
		return control.isDirty() ? count + 1 : count;
	}, 0);
});

watch(activeCount, (value) => emit('update:activeCount', value), { immediate: true });

// Never removes an entry: on a desktop <-> mobile swap both branches render the same keys and Vue may
// null the outgoing ref after mounting the incoming one, which would drop a live instance.
function setControl(key, instance) {
	if (instance) {
		controls[key] = instance;
	}
}

function onControlChange() {
	refreshVersion.value += 1;
}

defineExpose({
	controls,
	getActiveCount: () => activeCount.value,
	refreshActiveCount: () => {
		refreshVersion.value += 1;
	},
});
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.filter-controls-sheet {
	&__body {
		> * {
			width: 100%;
			max-width: none;
		}

		:deep(.v-select) {
			max-width: 100%;
		}
	}

	&__actions {
		gap: 8px;
		border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
	}
}

.filter-controls-panel {
	display: flex;
	flex-direction: column;
	height: 100%;

	:deep(.v-select) {
		max-width: 100%;
	}

	&__title {
		gap: 8px;
	}

	&__body {
		flex: 1 1 auto;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	&__toolbar {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	&__section-title {
		font-size: $font-size-small;
		font-weight: 600;
		text-transform: uppercase;
		color: rgba(var(--v-theme-on-surface), 0.6);
		margin-bottom: 8px;
	}

	&__section-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 12px;
	}

	&__actions {
		gap: 8px;
		border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
	}
}

.filter-controls-panel-content {
	width: 400px !important;
	max-width: 100% !important;
	border-radius: 0 !important;
}
</style>
